import { computed, ref } from "vue";
import { defineStore } from "pinia";
import { useCoins } from "@/composables/useCoins";
import { appendUnique } from "@/utils/history/appendUnique.js";

const halving = (res) => {
  if (!res) return 0;
  return res / 2;
};

export const HISTORY_LIMIT = 10;

export const useStakingStore = defineStore("staking", () => {
  const WITHDRAW_PERIOD = 2;
  const ORDER = ["ETH", "SOL"];

  const balance = ref(0);
  const setBalance = (data) => {
    balance.value = data;
  };

  const list = ref([]);
  const setList = (data) => {
    const cs = useCoins();
    const coins = cs.configCoins.value;
    const stats = cs.stats.value?.data ?? {};
    const fiat = cs.preferredCurrency.value;

    let arr = data ? data : [];
    arr = arr.map((a) => {
      const coin = coins.find((c) => c.id === a.setting.coin.toLowerCase());
      a.coin = coin;
      a.displayedPercent = Number(a.setting.percent) / 2;
      a.paid_capitalized_fiat =
        Number(a.paid_capitalized) *
        stats[a.setting.coin.toLowerCase()]?.[`${fiat}_rate`];
      return a;
    });
    list.value = arr;
    if (arr.length > 0) {
      setCurrentStaking(arr[0]);
    }
  };

  const currentStaking = ref({});
  const setCurrentStaking = (item) => {
    if (Object.keys(item).length === 0 && item.constructor === Object) {
      currentStaking.value = {};
      return;
    }
    const cs = useCoins();
    const coins = cs.configCoins.value;
    const stats = cs.stats.value?.data ?? {};
    const fiat = cs.preferredCurrency.value;
    const coin = coins.find((c) => c.id === item.setting?.coin?.toLowerCase());
    item.coin = coin;
    item.displayedPercent = Number(item.setting.percent) / 2;
    item.paid_capitalized_fiat =
      Number(item.paid_capitalized) *
      (stats[item.setting.coin.toLowerCase()]?.[`${fiat}_rate`] || 0);
    currentStaking.value = item;
  };

  const settings = ref([]);
  const setSettings = (data) => {
    const coins = useCoins().configCoins.value;
    settings.value =
      data.settings?.map((setting) => ({
        ...setting,
        displayed_percent: halving(setting.percent),
        coin: coins.find((c) => c.id === setting.coin.toLowerCase()),
      })) || [];
    if (!settings.value.length) {
      form.value.setting = null;
      form.value.coin = null;
      return;
    }

    form.value.setting = settings.value[0];
    form.value.coin = settings.value[0].coin || null;
    if (form.value.coin) {
      form.value.coin.setting = settings.value.filter(
        (a) => a.coin?.id === form.value.coin.id,
      );
    }
  };

  const form = ref({
    coin: null,
    setting: null,
    isCapitalized: true,
    mining_percent: 0,
    amount: null,
    withdraw_period: WITHDRAW_PERIOD,
  });

  const formUpdate = (key, value) => {
    form.value[key] = value;
  };

  const formClear = () => {
    form.value.amount = null;
    form.value.isCapitalized = true;
    form.value.mining_percent = 0;
    setSetting(settings.value[0]);
  };

  const setSetting = (setting) => {
    if (setting) {
      form.value.setting = setting;
      form.value.coin = form.value.setting.coin;
    }
  };

  const history = ref(null);
  // append — следующая порция истории (offset > 0)
  const setHistory = (data, { append = false } = {}) => {
    const items = data.operations ?? [];
    history.value = {
      total: data.total_count,
      items:
        append && history.value
          ? appendUnique(history.value.items, items)
          : items,
    };
  };

  const historyFilters = ref({
    coin: null,
    finished_after: null,
    finished_before: null,
    operation_type: null,
  });

  const setHistoryFilters = (filters = {}) => {
    historyFilters.value = {
      ...historyFilters.value,
      ...filters,
    };
  };

  const resetFilters = () => {
    historyFilters.value.coin = null;
    historyFilters.value.finished_after = null;
    historyFilters.value.finished_before = null;
    historyFilters.value.operation_type = null;
  };

  const isNoFilters = computed(() => {
    return (
      !historyFilters.value.coin &&
      !historyFilters.value.finished_after &&
      !historyFilters.value.finished_before &&
      !historyFilters.value.operation_type
    );
  });

  const historyLimit = HISTORY_LIMIT;

  return {
    ORDER,
    balance,
    setBalance,
    list,
    setList,
    settings,
    setSettings,
    currentStaking,
    setCurrentStaking,
    form,
    formUpdate,
    formClear,
    setSetting,
    history,
    setHistory,
    historyLimit,
    historyFilters,
    setHistoryFilters,
    resetFilters,
    isNoFilters,
  };
});
